import * as Sentry from "@sentry/react";
import { StrictMode } from "react";
import { createRoot, hydrateRoot } from "react-dom/client";
import "./i18n";
import { App } from "./App";
import { createBrowserRouter, matchRoutes } from "react-router";
import { createRoutes } from "./router";
import { applyPageData, createRootStore } from "./stores/rootStore";
import { i18next } from "./i18n";
import { isSsrPath } from "./ssr/policy";
import type { BootstrapData, PageData } from "./ssr/data";
import { initAnalytics, track } from "./lib/analytics";
import { captureReferralFromPath, captureReferralFromSearch } from "./lib/referralCapture";
import { reportShareLanding } from "./lib/shareLanding";

const sentryDsn = import.meta.env.VITE_SENTRY_DSN?.trim();

if (sentryDsn) {
	Sentry.init({
		dsn: sentryDsn,
		environment: import.meta.env.VITE_SENTRY_ENVIRONMENT ?? import.meta.env.MODE,
	});
}

initAnalytics();

// Runs before the first render so a hard load captures the invite code exactly once. The path is
// read first: shared links are `/open/invite/<code>`, and the router's forward to `/?ref=<code>`
// is a client-side navigation that lands here too late for the query to be seen.
const capturedFromPath = captureReferralFromPath(window.location.pathname);
const capturedReferral = capturedFromPath ?? captureReferralFromSearch(window.location.search);
if (capturedReferral) {
	track("referral_captured", { source: capturedFromPath ? "invite_path" : "ref_query" });
}

// Same timing constraint: the shared link's l_data/utm live on the landing URL, which the router
// rewrites away. Best-effort — closes the share-attribution loop the app leaves to the web side.
reportShareLanding();

const sentryRootOptions = sentryDsn
	? {
			onUncaughtError: Sentry.reactErrorHandler(),
			onCaughtError: Sentry.reactErrorHandler(),
			onRecoverableError: Sentry.reactErrorHandler(),
		}
	: undefined;

const root = document.getElementById("root")!;
const payload = document.getElementById("__SSR_DATA__");
const bootstrap = payload?.textContent ? JSON.parse(payload.textContent) as BootstrapData : null;
const store = createRootStore(bootstrap?.page);
const routes = createRoutes(async ({ request }) => {
	const url = new URL(request.url);
	if (!isSsrPath(url.pathname)) return null;
	try {
		const pageQuery = new URLSearchParams({ path: url.pathname });
		for (const key of ["category", "tab", "sort"]) {
			const value = url.searchParams.get(key);
			if (value) pageQuery.set(key, value);
		}
		const response = await fetch(`/__page-data?${pageQuery}`, { signal: request.signal });
		if (!response.ok) return null;
		const data = await response.json() as PageData;
		request.signal.throwIfAborted();
		return data;
	} catch { return null; }
});
// Resolve the same matched lazy modules before hydration, so route chunks cannot replace SSR
// markup with a fallback spinner while React is attaching event handlers.
if (bootstrap) {
	await Promise.all((matchRoutes(routes, window.location.pathname) ?? []).map(async ({ route }) => {
		if (typeof route.lazy === "function") {
			Object.assign(route, await route.lazy());
			delete route.lazy;
		}
	}));
}
// A CSR fallback must mount immediately instead of repeating the server's failed loader.
const router = createBrowserRouter(routes, { hydrationData: { loaderData: { page: bootstrap?.page ?? null } } });
let appliedPage = bootstrap?.page;
// Only committed navigations may update shared state; canceled loaders cannot change the viewer.
router.subscribe((state) => {
	const data = state.loaderData.page as PageData | null | undefined;
	if (state.navigation.state !== "idle" || !data || data === appliedPage) return;
	appliedPage = data;
	applyPageData(store, data);
	void i18next.changeLanguage(data.locale);
});
const app = (
	<StrictMode>
		<App router={router} store={store} />
	</StrictMode>
);

if (bootstrap) hydrateRoot(root, app, sentryRootOptions);
else createRoot(root, sentryRootOptions).render(app);
